import { useMutation, useQuery } from "@apollo/client/react";
import type { FragmentData } from "@gqlb/core";
import Fuse from "fuse.js";
import gsap from "gsap";
import { ArrowRight, Check, ChevronDown, ChevronRight, Search, ShoppingBag, X } from "lucide-react";
import type { InferGetServerSidePropsType, NextPage } from "next";
import Head from "next/head";
import NextLink from "next/link";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { b } from "@/b";
import { HEADER_FRAGMENT } from "@/components/Header";
import { PageLayout } from "@/components/PageLayout";
import { FulfillmentConflictSheet } from "@/components/pages/menu/FulfillmentConflictSheet";
import { FulfillmentModeSelector } from "@/components/pages/menu/FulfillmentModeSelector";
import type { ReorderableItem } from "@/components/pages/menu/PreviouslyOrderedSection";
import {
  PreviouslyOrdered_Fragment,
  PreviouslyOrderedSection,
} from "@/components/pages/menu/PreviouslyOrderedSection";
import { QuickAddModal, QuickAddModal_Fragment } from "@/components/pages/menu/QuickAddModal";
import { ProductCard, ProductCard_Fragment } from "@/components/pages/product/ProductCard";
import { CartPreviewSection_Fragment, PRODUCT_ADD_TO_CART_MUTATION } from "@/graphql/fragments";
import { SanityText } from "@/sanity/components/SanityText";
import { popupMiddleware } from "@/sanity/popup-middleware";
import { getApolloSSRProps, useHydratedQuery } from "@/util/apollo-client";
import {
  CartFulfillment_Fragment,
  FULFILLMENT_LABELS,
  fulfillmentNoun,
  fulfillmentSubtitle,
  isValidFulfillmentMode,
  useFulfillmentMode,
} from "@/util/fulfillment";
import { Hook_IsSubscribedToVIP_Fragment, useIsSubscribedToVIP } from "@/util/hook";

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

// Sections only carry product ids; full product data lives once in the flat
// `products` list and is joined client-side (see `sections` memo below). This
// keeps each product out of the SSR payload N times (once per section).
const MenuProducts_Fragment = b.fragment("MenuPageProducts", "Menu", (b) => [
  b.products((b) => [
    b.product((b) => [b.__fragment(ProductCard_Fragment), b.__fragment(QuickAddModal_Fragment)]),
    b.callout(),
  ]),
  b.tags((b) => [b.id(), b.name()]),
  b.sections((b) => [
    b.id(),
    b.title(),
    b.description((b) => [b.json()]),
    b.products((b) => [b.product((b) => [b.id()]), b.callout()]),
  ]),
]);

// The server filters the menu to the cart's resolved fulfillment mode and
// reports how many meals that hid; the client never filters by mode itself.
const MENU_QUERY = b.query("Menu", { clock: "String" }, (b, v) => [
  b.menu({ clock: v.clock }, (b) => [b.__fragment(MenuProducts_Fragment), b.hiddenProductCount()]),
  b.viewer((b) => [b.id(), b.cart((b) => [b.__fragment(CartFulfillment_Fragment)])]),
  b.pickupLocations((b) => [b.id()]),
  b.__fragment(Hook_IsSubscribedToVIP_Fragment),
  b.__fragment(PreviouslyOrdered_Fragment),
  b.__fragment(HEADER_FRAGMENT),
  b.__fragment(CartPreviewSection_Fragment),
]);

const MUTATION_ADD_COUPON = b.mutation("MenuAddCoupon", { code: "String!" }, (b, v) => [
  b.viewer((b) => [b.cart((b) => [b.applyCoupon({ code: v.code }, (b) => [b.totalQuantity()])])]),
]);

// Fallback query for products not on the menu (e.g. swag linked via /product/:slug)
const PRODUCT_BY_SLUG_QUERY = b.query("MenuProductBySlug", { slug: "String!" }, (b, v) => [
  b.product({ selector: { slug: v.slug } }, (b) => [
    b.__fragment(ProductCard_Fragment),
    b.__fragment(QuickAddModal_Fragment),
  ]),
]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MenuData = FragmentData<typeof MenuProducts_Fragment>;
type Product = MenuData["products"][number];
type Section = Omit<MenuData["sections"][number], "products"> & {
  products: readonly Product[];
};
type CartData = FragmentData<typeof CartPreviewSection_Fragment>["viewer"]["cart"];
type CartItem = CartData["items"][number];
type CartProductItem = Extract<CartItem, { __typename: "CartItemProduct" }>;

const PREVIEW_COUNT = 6;

// ---------------------------------------------------------------------------
// Compact Hero
// ---------------------------------------------------------------------------

// (Hero removed)

// ---------------------------------------------------------------------------
// Toolbar — title + search, then the fulfillment selector with its caption.
// Every control here shares one vocabulary with the category chips below:
// a 40px rounded-full pill on gray-100, selected = mint.
// ---------------------------------------------------------------------------

const MenuToolbar: React.FC<{
  searchQuery: string;
  onSearchChange: (q: string) => void;
  selector: React.ReactNode;
  /** One short sentence about the selected mode, e.g. "Delivered to your door". */
  caption: string;
  /** Second sentence, muted: e.g. "2 meals aren't available for nationwide shipping." */
  captionNote?: string | null;
}> = ({ searchQuery, onSearchChange, selector, caption, captionNote }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchVisible = mobileSearchOpen || searchQuery.length > 0;

  const searchField = (
    <div className="relative w-full lg:w-72">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-gray-400 pointer-events-none" />
      <input
        ref={inputRef}
        type="search"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search meals"
        aria-label="Search meals"
        className="w-full h-10 pl-11 pr-10 rounded-full bg-gray-100 text-sm text-brand-black placeholder:text-gray-500 outline-none focus:bg-white focus:ring-2 focus:ring-brand-mint transition-colors [&::-webkit-search-cancel-button]:hidden"
      />
      {searchQuery && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            onSearchChange("");
            inputRef.current?.focus();
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center size-6 rounded-full text-gray-500 hover:bg-gray-200 hover:text-brand-black cursor-pointer transition-colors"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <div className="px-6 lg:px-8 pt-8 pb-4 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-brand-black tracking-tight">Menu</h1>
        <div className="hidden lg:block">{searchField}</div>
        <button
          type="button"
          aria-label={searchVisible ? "Hide search" : "Search meals"}
          aria-expanded={searchVisible}
          onClick={() => {
            const next = !searchVisible;
            setMobileSearchOpen(next);
            if (!next) onSearchChange("");
            else requestAnimationFrame(() => inputRef.current?.focus());
          }}
          className={`lg:hidden flex items-center justify-center size-10 rounded-full cursor-pointer transition-colors active:scale-[0.98] ${
            searchVisible
              ? "bg-brand-mint text-brand-black"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          {searchVisible ? <X className="size-4" /> : <Search className="size-4" />}
        </button>
      </div>

      {searchVisible && <div className="lg:hidden">{searchField}</div>}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        {selector}
        <p className="text-sm text-gray-500 text-center sm:text-left sm:min-w-0">
          {caption}
          {captionNote && <span className="text-gray-400"> {captionNote}</span>}
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sidebar (desktop only)
// ---------------------------------------------------------------------------

const Sidebar: React.FC<{
  sections: readonly Section[];
  activeSectionId: string | null;
  onCategoryClick: (sectionId: string) => void;
}> = ({ sections, activeSectionId, onCategoryClick }) => (
  <aside className="hidden flex-col w-52 shrink-0 h-screen sticky top-[100px] bg-white border-r border-gray-100 z-30 max-h-[calc(100vh-100px)]">
    <div className="px-4 pt-6 pb-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
        Categories
      </p>
    </div>
    <nav className="flex-1 overflow-y-auto px-2 space-y-0.5">
      {sections.map((section) => {
        const count = section.products.length;
        if (count === 0) return null;
        const active = section.id === activeSectionId;
        return (
          <button
            key={section.id}
            type="button"
            onClick={() => onCategoryClick(section.id)}
            className={`cursor-pointer w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center justify-between transition-colors ${
              active
                ? "text-brand-black font-semibold border-l-2 border-brand-mint bg-brand-mint/10"
                : "text-gray-600 font-medium border-l-2 border-transparent hover:text-brand-black hover:bg-gray-50"
            }`}
          >
            {section.title}
            <span className={`text-xs ${active ? "text-brand-black" : "text-gray-400"}`}>
              {count}
            </span>
          </button>
        );
      })}
    </nav>
  </aside>
);

// ---------------------------------------------------------------------------
// CategoryBar — horizontal scrollable pill bar
// ---------------------------------------------------------------------------

const CategoryBar: React.FC<{
  sections: readonly Section[];
  activeSectionId: string | null;
  onCategoryClick: (sectionId: string) => void;
}> = ({ sections, activeSectionId, onCategoryClick }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Auto-scroll active pill into view when active category changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeSectionId triggers the scroll even though we read from refs
  useEffect(() => {
    if (activeRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const pill = activeRef.current;
      const left = pill.offsetLeft - container.offsetWidth / 2 + pill.offsetWidth / 2;
      container.scrollTo({ left, behavior: "smooth" });
    }
  }, [activeSectionId]);

  return (
    <div className="sticky top-(--vm-header-height,120px) z-30 bg-white/90 backdrop-blur-lg border-b border-gray-100/80">
      <div ref={scrollRef} className="flex gap-2 overflow-x-auto px-6 lg:px-8 py-3 no-scrollbar">
        {sections.map((section) => {
          if (section.products.length === 0) return null;
          const active = section.id === activeSectionId;
          return (
            <button
              key={section.id}
              ref={active ? activeRef : undefined}
              type="button"
              onClick={() => onCategoryClick(section.id)}
              className={`shrink-0 h-10 px-4 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                active
                  ? "bg-brand-mint text-brand-black"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {section.title}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// MobileCarousel — horizontal scroll with fade hint + chevron
// ---------------------------------------------------------------------------

const MobileCarousel: React.FC<{
  products: readonly Product[];
  onQuickAdd: (p: Product) => void;
  cartProductIds: Set<string>;
  lastViewedSlug: string | null;
}> = ({ products, onQuickAdd, cartProductIds, lastViewedSlug }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      if (el.scrollLeft > 20) setScrolled(true);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="lg:hidden relative">
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        <div className="shrink-0 w-2" aria-hidden="true" />
        {products.map((p) => (
          <div
            key={p.product.id}
            data-product-slug={p.product.slug}
            className="w-[85vw] max-w-[360px] shrink-0"
          >
            <ProductCard
              data={p.product}
              callout={p.callout ?? undefined}
              onClick={() => onQuickAdd(p)}
              inCart={cartProductIds.has(p.product.id)}
              lastViewed={p.product.slug === lastViewedSlug}
            />
          </div>
        ))}
        <div className="shrink-0 w-2" aria-hidden="true" />
      </div>
      {/* Scroll hint — fades away after first scroll */}
      {!scrolled && products.length > 1 && (
        <div className="absolute right-0 top-0 bottom-2 w-14 flex items-center justify-end pr-2 bg-linear-to-l from-white via-white/80 to-transparent pointer-events-none z-10">
          <div className="flex items-center justify-center size-8 rounded-full bg-white/90 shadow text-gray-700">
            <ChevronRight className="size-5" />
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CategorySection — 3-col desktop grid, large horizontal scroll mobile
// ---------------------------------------------------------------------------

const CategorySection: React.FC<{
  section: Section;
  forceExpanded: boolean;
  onQuickAdd: (p: Product) => void;
  cartProductIds: Set<string>;
  lastViewedSlug: string | null;
}> = ({ section, forceExpanded, onQuickAdd, cartProductIds, lastViewedSlug }) => {
  const products = section.products;
  const [expanded, setExpanded] = useState(false);
  const isExpanded = expanded || forceExpanded;
  const hasMore = products.length > PREVIEW_COUNT;

  return (
    <section
      id={`category-${section.id}`}
      className="scroll-mt-[calc(var(--vm-header-height,120px)+48px)] mb-14 md:mb-16 last:mb-0"
    >
      <div className="px-6 lg:px-8 mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-lg md:text-xl font-bold text-brand-black tracking-tight">
            {section.title}
          </h2>
          <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">
            {products.length} {products.length === 1 ? "meal" : "meals"}
          </span>
        </div>
        {section.description?.json && (
          <div className="mt-2 text-sm text-gray-500 max-w-2xl">
            <SanityText content={JSON.parse(section.description.json)} />
          </div>
        )}
      </div>

      {/* Mobile: large horizontal scroll with scroll hint */}
      <MobileCarousel
        products={products}
        onQuickAdd={onQuickAdd}
        cartProductIds={cartProductIds}
        lastViewedSlug={lastViewedSlug}
      />

      {/* Desktop: 3-col grid with peek + expand */}
      <div className="hidden lg:block px-8">
        {isExpanded ? (
          <div className="grid grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.product.id} data-product-slug={p.product.slug}>
                <ProductCard
                  data={p.product}
                  callout={p.callout ?? undefined}
                  onClick={() => onQuickAdd(p)}
                  inCart={cartProductIds.has(p.product.id)}
                  lastViewed={p.product.slug === lastViewedSlug}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="relative">
            <div className="grid grid-cols-3 gap-6">
              {products.slice(0, PREVIEW_COUNT).map((p) => (
                <div key={p.product.id} data-product-slug={p.product.slug}>
                  <ProductCard
                    data={p.product}
                    callout={p.callout ?? undefined}
                    onClick={() => onQuickAdd(p)}
                    inCart={cartProductIds.has(p.product.id)}
                    lastViewed={p.product.slug === lastViewedSlug}
                  />
                </div>
              ))}
            </div>
            {hasMore && (
              <div className="relative mt-6 h-48 overflow-hidden">
                <div className="grid grid-cols-3 gap-6 pointer-events-none">
                  {products.slice(PREVIEW_COUNT, PREVIEW_COUNT + 3).map((p) => (
                    <div key={p.product.id} data-product-slug={p.product.slug}>
                      <ProductCard
                        data={p.product}
                        callout={p.callout ?? undefined}
                        inCart={cartProductIds.has(p.product.id)}
                        lastViewed={p.product.slug === lastViewedSlug}
                      />
                    </div>
                  ))}
                </div>
                {/* Entire fade area is the click target */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    const scrollY = window.scrollY;
                    setExpanded(true);
                    requestAnimationFrame(() => window.scrollTo(0, scrollY));
                  }}
                  className="absolute inset-0 cursor-pointer flex items-start justify-center pt-12 group"
                  style={{ background: "linear-gradient(to top, white 30%, transparent 100%)" }}
                >
                  <span className="flex items-center gap-2 px-6 py-2.5 rounded-full border border-gray-200 bg-white text-base font-semibold text-gray-600 group-hover:text-brand-black group-hover:border-gray-300 group-hover:shadow-sm transition-all">
                    View all {products.length} meals
                    <ChevronDown className="size-5" />
                  </span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Search Results
// ---------------------------------------------------------------------------

const SearchResultsGrid: React.FC<{
  results: readonly Product[];
  query: string;
  onClear: () => void;
  onQuickAdd: (p: Product) => void;
  cartProductIds: Set<string>;
  lastViewedSlug: string | null;
  /** e.g. "3 meals are hidden for Nationwide shipping." */
  hiddenNote?: string | null;
}> = ({ results, query, onClear, onQuickAdd, cartProductIds, lastViewedSlug, hiddenNote }) => (
  <div className="px-6 lg:px-8 pb-10">
    <div className="flex items-baseline justify-between mb-6">
      <p className="text-base font-medium text-brand-black">
        {results.length} {results.length === 1 ? "result" : "results"} for &ldquo;{query}&rdquo;
      </p>
      <button
        type="button"
        onClick={onClear}
        className="cursor-pointer text-sm text-gray-500 hover:text-brand-mint transition-colors flex items-center gap-1"
      >
        <X className="size-3.5" /> Clear
      </button>
    </div>
    {results.length === 0 ? (
      <p className="text-gray-400 py-16 text-center">
        No meals found. Try a different search.
        {hiddenNote && ` ${hiddenNote}`}
      </p>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {results.map((p) => (
          <div key={p.product.id} data-product-slug={p.product.slug}>
            <ProductCard
              data={p.product}
              callout={p.callout ?? undefined}
              onClick={() => onQuickAdd(p)}
              inCart={cartProductIds.has(p.product.id)}
              lastViewed={p.product.slug === lastViewedSlug}
            />
          </div>
        ))}
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Bottom Bar — mobile: compact cart pill (left); desktop: centered cart bar
// ---------------------------------------------------------------------------

const BottomBar: React.FC<{
  cart: CartData;
  /** e.g. "Pickup" — shown on the desktop bar so the mode is visible while scrolling. */
  modeLabel?: string;
}> = ({ cart, modeLabel }) => {
  const productItems = cart.items.filter(
    (i): i is CartProductItem => i.__typename === "CartItemProduct",
  );
  const hasCartItems = productItems.length > 0;
  if (!hasCartItems) return null;

  const canCheckout = cart.totalQuantity >= cart.productMinimum;
  const remaining = cart.productMinimum - cart.totalQuantity;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 pointer-events-none">
      {/* Mobile: compact pill, left-aligned */}
      <div className="lg:hidden p-4 pointer-events-auto">
        <NextLink
          href="/cart"
          data-cart-pill="mobile"
          className={`inline-flex items-center gap-2.5 rounded-full shadow-lg px-4 py-2.5 no-underline ${
            canCheckout
              ? "bg-brand-mint text-brand-black"
              : "bg-white text-brand-black border border-gray-200"
          }`}
        >
          <ShoppingBag className="size-5 shrink-0" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold leading-tight">
              {canCheckout ? "Checkout" : `${cart.totalQuantity} meals`}
            </span>
            {!canCheckout && (
              <span
                className={`text-[10px] leading-tight ${canCheckout ? "text-brand-black/50" : "text-gray-500"}`}
              >
                Add {remaining} more to checkout
              </span>
            )}
          </div>
          <ArrowRight className="size-4" />
        </NextLink>
      </div>

      {/* Desktop: centered floating cart bar */}
      <div className="hidden lg:block p-4 pointer-events-auto">
        <div className="max-w-2xl mx-auto">
          <div
            data-cart-pill="desktop"
            className="bg-white rounded-2xl shadow-lg border border-gray-200 px-5 py-4 flex items-center gap-4"
          >
            <ShoppingBag className="size-5 text-gray-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-brand-black text-sm font-semibold">
                {cart.totalQuantity} {cart.totalQuantity === 1 ? "meal" : "meals"} in cart
                {modeLabel && <span className="text-gray-400 font-normal"> · {modeLabel}</span>}
              </p>
              {!canCheckout && (
                <p className="text-gray-500 text-xs mt-0.5">Add {remaining} more to checkout</p>
              )}
            </div>
            <NextLink
              href="/cart"
              className={`shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors no-underline ${
                canCheckout
                  ? "bg-brand-mint text-brand-black hover:bg-brand-mint/90"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {canCheckout ? "Checkout" : "View Cart"}
              <ArrowRight className="size-4" />
            </NextLink>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Page: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const router = useRouter();
  const { data } = useHydratedQuery(props);
  const isSubscribedToVIP = useIsSubscribedToVIP(data, true);

  const [searchQuery, setSearchQuery] = useState("");
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [allExpanded, _setAllExpanded] = useState(false);

  // Fly-to-cart animation after adding from quick-add modal
  const animateAddToCart = useCallback((info: { imageUrl: string; rect: DOMRect }) => {
    const { imageUrl, rect: sourceRect } = info;

    // Create floating product image immediately at source position
    const img = document.createElement("img");
    img.src = imageUrl;
    img.style.cssText = `
      position: fixed;
      z-index: 9999;
      left: ${sourceRect.left}px;
      top: ${sourceRect.top}px;
      width: ${sourceRect.width}px;
      height: ${sourceRect.height}px;
      object-fit: cover;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.25);
      pointer-events: none;
    `;
    document.body.appendChild(img);

    // Wait for React to re-render (cart pill may not exist yet on first add)
    const runAnimation = (attempt: number) => {
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      const cartEl = document.querySelector<HTMLElement>(
        isDesktop ? "[data-cart-pill='desktop']" : "[data-cart-pill='mobile']",
      );

      if (!cartEl && attempt < 30) {
        requestAnimationFrame(() => runAnimation(attempt + 1));
        return;
      }

      if (!cartEl) {
        // Cart pill never appeared — just fade out in place
        gsap.to(img, { opacity: 0, duration: 0.3, onComplete: () => img.remove() });
        return;
      }

      const cartRect = cartEl.getBoundingClientRect();
      const tl = gsap.timeline();

      // Shrink and fade toward cart
      tl.to(img, {
        left: cartRect.left + cartRect.width / 2 - 30,
        top: cartRect.top + cartRect.height / 2 - 30,
        width: 60,
        height: 60,
        opacity: 0.6,
        borderRadius: "50%",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        duration: 0.5,
        ease: "power2.inOut",
        onComplete: () => img.remove(),
      });

      // Gentle bump on the cart pill
      tl.to(cartEl, {
        keyframes: [
          { scale: 1.08, duration: 0.12 },
          { scale: 1, duration: 0.2, ease: "back.out(1.5)" },
        ],
      });
    };

    requestAnimationFrame(() => runAnimation(0));
  }, []);

  const [applyCoupon] = useMutation(MUTATION_ADD_COUPON.document());
  const [couponToast, setCouponToast] = useState<{ code: string; visible: boolean } | null>(null);
  useEffect(() => {
    if (router.query.coupon) {
      const code = String(router.query.coupon);
      applyCoupon({ variables: { code } })
        .then(() => {
          setCouponToast({ code, visible: true });
          setTimeout(() => setCouponToast((t) => (t ? { ...t, visible: false } : null)), 4000);
          setTimeout(() => setCouponToast(null), 4500);
        })
        .catch(console.error);
      // Clean the URL without reloading
      router.replace("/menu", undefined, { shallow: true });
    }
  }, [router.query.coupon, applyCoupon, router]);

  // Scroll to category section when navigating from homepage category links
  const tagsParam = router.query.tags;
  const routerReplace = router.replace;
  useEffect(() => {
    if (typeof tagsParam === "string" && tagsParam) {
      // Wait for DOM to render category sections
      requestAnimationFrame(() => {
        const el = document.getElementById(`category-${tagsParam}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
          setActiveSectionId(tagsParam);
        }
      });
      // Clean the URL without reloading
      routerReplace("/menu", undefined, { shallow: true });
    }
  }, [tagsParam, routerReplace]);

  const menu = data.menu as MenuData | null;
  const allProducts = useMemo(() => menu?.products ?? [], [menu]);
  const rawSections = menu?.sections;
  // Join section product ids back to the full entries from the flat list
  const sections: readonly Section[] = useMemo(() => {
    const byId = new Map(allProducts.map((p) => [p.product.id, p]));
    return (rawSections ?? []).map((section) => ({
      ...section,
      products: section.products.flatMap((sp) => {
        const full = byId.get(sp.product.id);
        if (!full) return [];
        return [sp.callout != null ? { ...full, callout: sp.callout } : full];
      }),
    }));
  }, [rawSections, allProducts]);
  const cart = data.viewer.cart;
  const hasCartItems = cart.items.some((i) => i.__typename === "CartItemProduct");

  // Fulfillment mode — resolved by the server; switching refetches the
  // (server-filtered) menu. Conflicting cart items come back as a preview
  // and are confirmed in the sheet before removal.
  const fulfillment = useFulfillmentMode(cart.fulfillment, { refetchQueries: ["Menu"] });
  const hiddenProductCount = data.menu?.hiddenProductCount ?? 0;
  const pickupLocationCount = data.pickupLocations.length;

  const didReportDefault = useRef(false);
  useEffect(() => {
    if (didReportDefault.current) return;
    didReportDefault.current = true;
    posthog.capture("fulfillment_mode_defaulted", {
      mode: fulfillment.mode,
      source: fulfillment.source,
    });
  }, [fulfillment.mode, fulfillment.source]);

  // /menu?mode=NATIONWIDE deep links (marketing) pick the mode once, then clean the URL.
  const modeParam = router.query.mode;
  const selectMode = fulfillment.select;
  useEffect(() => {
    if (typeof modeParam !== "string") return;
    const requested = modeParam.toUpperCase();
    if (isValidFulfillmentMode(requested)) selectMode(requested, "url");
    routerReplace("/menu", undefined, { shallow: true });
  }, [modeParam, selectMode, routerReplace]);

  // URL-driven quick-add modal: /product/:slug rewrites to /menu?product=:slug
  // Use the SSR prop until the router is ready to avoid a hydration gap where
  // router.query is empty, which would cause the modal to "snap" in after load.
  const productSlug = router.isReady
    ? typeof router.query.product === "string"
      ? router.query.product
      : null
    : props.initialProductSlug;
  const menuProduct = useMemo(() => {
    if (!productSlug) return null;
    return allProducts.find((p) => p.product.slug === productSlug) ?? null;
  }, [productSlug, allProducts]);

  // Fallback: fetch product by slug if not found in menu (e.g. swag items)
  const { data: fallbackProductData } = useQuery(PRODUCT_BY_SLUG_QUERY.document(), {
    variables: { slug: productSlug ?? "" },
    skip: !productSlug || !!menuProduct,
  });

  const quickAddProduct = useMemo(() => {
    if (menuProduct) return menuProduct;
    if (fallbackProductData?.product) {
      return { product: fallbackProductData.product, callout: null };
    }
    return null;
  }, [menuProduct, fallbackProductData]);

  const openQuickAdd = useCallback(
    (p: Product) => {
      router.push(`/product/${p.product.slug}`, undefined, { shallow: true });
    },
    [router],
  );

  const [lastViewedSlug, setLastViewedSlug] = useState<string | null>(null);

  const closeQuickAdd = useCallback(() => {
    if (productSlug) setLastViewedSlug(productSlug);
    router.push("/menu", undefined, { shallow: true });
  }, [router, productSlug]);

  // On initial load with a product slug, scroll to that product's card
  const hasScrolledToProduct = useRef(false);
  useEffect(() => {
    if (!productSlug || hasScrolledToProduct.current) return;
    hasScrolledToProduct.current = true;
    setLastViewedSlug(productSlug);

    // Wait a tick for DOM to render
    requestAnimationFrame(() => {
      const card = document.querySelector<HTMLElement>(
        `[data-product-slug="${CSS.escape(productSlug)}"]`,
      );
      if (!card) return;

      // If the card is inside a horizontal scroll container (mobile carousel), scroll it into view horizontally
      const scrollParent = card.closest("[class*='overflow-x-auto']") as HTMLElement | null;
      if (scrollParent) {
        scrollParent.scrollTo({ left: card.offsetLeft - 8, behavior: "instant" });
      }

      // Scroll the page vertically to the card
      card.scrollIntoView({ block: "center", behavior: "instant" });
    });
  }, [productSlug]);

  const cartProductIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of cart.items) {
      if (item.__typename === "CartItemProduct") {
        ids.add(item.product.id);
      }
    }
    return ids;
  }, [cart.items]);

  // Previously ordered items — included in SSR query via PreviouslyOrdered_Fragment
  const previouslyOrderedItems =
    (data.viewer.account?.reorderableItems as ReorderableItem[] | undefined) ?? [];

  const [reorderingKey, setReorderingKey] = useState<string | null>(null);
  const reorderingRef = useRef<string | null>(null);
  const [addToCart] = useMutation(PRODUCT_ADD_TO_CART_MUTATION.document());

  const handleReorder = useCallback(
    (item: ReorderableItem, info: { imageUrl: string; rect: DOMRect }) => {
      if (reorderingRef.current) return;
      reorderingRef.current = item.dedupeKey;
      setReorderingKey(item.dedupeKey);
      addToCart({
        variables: {
          productId: item.product.id,
          quantity: 1,
          selections: item.selections,
        },
        refetchQueries: ["Menu"],
        awaitRefetchQueries: true,
      })
        .then(() => {
          animateAddToCart(info);
        })
        .catch(() => {
          router.push(`/product/${item.product.slug}`, undefined, { shallow: true });
        })
        .finally(() => {
          reorderingRef.current = null;
          setReorderingKey(null);
        });
    },
    [addToCart, animateAddToCart, router],
  );

  // Fuse.js index — name (high priority) + description (lower)
  const fuse = useMemo(
    () =>
      new Fuse(allProducts as Product[], {
        keys: [
          { name: "product.name", weight: 2 },
          { name: "product.tags.name", weight: 1 },
        ],
        threshold: 0.35,
        ignoreLocation: true,
        minMatchCharLength: 2,
        getFn: (obj, path) => {
          const value = Fuse.config.getFn(obj, path);
          if (typeof value === "string") return value.replace(/[-_]/g, " ");
          if (Array.isArray(value))
            return value.map((v) => (typeof v === "string" ? v.replace(/[-_]/g, " ") : v));
          return value;
        },
      }),
    [allProducts],
  );

  // Exact (substring) match on description, separate from fuzzy
  const fuseExactDesc = useMemo(
    () =>
      new Fuse(allProducts as Product[], {
        keys: [{ name: "product.description", weight: 1 }],
        threshold: 0,
        ignoreLocation: true,
        minMatchCharLength: 3,
      }),
    [allProducts],
  );

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.trim();
    const fuzzyHits = fuse.search(q).map((r) => r.item);
    const descHits = fuseExactDesc.search(q).map((r) => r.item);
    // Merge, dedupe, fuzzy results first
    const seen = new Set<string>();
    const merged: Product[] = [];
    for (const item of [...fuzzyHits, ...descHits]) {
      if (!seen.has(item.product.id)) {
        seen.add(item.product.id);
        merged.push(item);
      }
    }
    return merged;
  }, [fuse, fuseExactDesc, searchQuery]);

  useEffect(() => {
    if (searchResults) return;
    const sectionEls = sections
      .map((s) => document.getElementById(`category-${s.id}`))
      .filter(Boolean) as HTMLElement[];
    if (sectionEls.length === 0) return;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActiveSectionId(e.target.id.replace("category-", ""));
        }
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 },
    );
    for (const s of sectionEls) obs.observe(s);
    return () => obs.disconnect();
  }, [sections, searchResults]);

  const scrollToCategory = useCallback((sectionId: string) => {
    setSearchQuery("");
    setActiveSectionId(sectionId);
    document.getElementById(`category-${sectionId}`)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Bottom padding: mobile always has pills bar (~56px), + cart row (~60px) if items. Desktop: cart bar (~80px) if items.
  const bottomPad = hasCartItems ? "pb-24 lg:pb-28" : "pb-8";

  return (
    <>
      <Head>
        <title>Menu | Visionary Meals</title>
        <meta
          name="description"
          content="Explore our delicious gluten-free, farm to table, fully-prepped meals!"
        />
      </Head>
      <PageLayout data={data} noPadding>
        {/* Coupon applied toast */}
        {couponToast && (
          <div
            className={`fixed top-32 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
              couponToast.visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
            }`}
          >
            <div className="flex items-center gap-3 rounded-2xl bg-white border border-brand-mint/40 shadow-xl shadow-brand-mint/10 px-6 py-4">
              <span className="flex items-center justify-center size-7 rounded-full bg-brand-mint">
                <Check className="size-4 text-brand-black" strokeWidth={3} />
              </span>
              <span className="text-base font-semibold text-brand-black">
                Coupon <span className="text-brand-mint">{couponToast.code}</span> applied
              </span>
            </div>
          </div>
        )}

        <div className="flex min-h-screen bg-white">
          <Sidebar
            sections={sections}
            activeSectionId={activeSectionId}
            onCategoryClick={scrollToCategory}
          />

          <div className="flex-1 min-w-0">
            <MenuToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selector={
                <FulfillmentModeSelector
                  className="w-full sm:w-auto"
                  value={fulfillment.mode}
                  onChange={(mode) => fulfillment.select(mode)}
                  availableModes={fulfillment.availableModes}
                  pending={fulfillment.pending}
                />
              }
              caption={fulfillmentSubtitle(
                fulfillment.mode,
                fulfillment.source,
                pickupLocationCount,
              )}
              captionNote={
                hiddenProductCount > 0
                  ? `${hiddenProductCount} ${hiddenProductCount === 1 ? "meal isn't" : "meals aren't"} available for ${fulfillmentNoun(fulfillment.mode).toLowerCase()}.`
                  : null
              }
            />
            <CategoryBar
              sections={sections}
              activeSectionId={activeSectionId}
              onCategoryClick={scrollToCategory}
            />
            <div className={`pb-8 ${bottomPad}`}>
              {!searchResults && previouslyOrderedItems.length > 0 && (
                <PreviouslyOrderedSection
                  items={previouslyOrderedItems}
                  reorderingKey={reorderingKey}
                  onReorder={handleReorder}
                />
              )}
              {searchResults ? (
                <SearchResultsGrid
                  results={searchResults}
                  query={searchQuery}
                  onClear={() => setSearchQuery("")}
                  onQuickAdd={openQuickAdd}
                  cartProductIds={cartProductIds}
                  lastViewedSlug={lastViewedSlug}
                  hiddenNote={
                    hiddenProductCount > 0
                      ? `${hiddenProductCount} ${hiddenProductCount === 1 ? "meal is" : "meals are"} hidden for ${fulfillmentNoun(fulfillment.mode)}.`
                      : null
                  }
                />
              ) : (
                sections.map((section) => {
                  if (section.products.length === 0) return null;
                  return (
                    <CategorySection
                      key={section.id}
                      section={section}
                      forceExpanded={allExpanded}
                      onQuickAdd={openQuickAdd}
                      cartProductIds={cartProductIds}
                      lastViewedSlug={lastViewedSlug}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>

        <BottomBar cart={cart} modeLabel={FULFILLMENT_LABELS[fulfillment.mode]} />
      </PageLayout>

      {fulfillment.conflict && (
        <FulfillmentConflictSheet
          conflict={fulfillment.conflict}
          busy={fulfillment.pending}
          onRemoveAndSwitch={fulfillment.confirmConflict}
          onKeep={fulfillment.dismissConflict}
        />
      )}

      {quickAddProduct && (
        <QuickAddModal
          product={quickAddProduct.product}
          callout={quickAddProduct.callout ?? undefined}
          isSubscribedToVIP={isSubscribedToVIP}
          fulfillmentMode={fulfillment.mode}
          availableModes={fulfillment.availableModes}
          onSwitchMode={(mode) => fulfillment.select(mode, "quick_add_modal")}
          onClose={closeQuickAdd}
          onAddedToCart={animateAddToCart}
        />
      )}
    </>
  );
};

export default Page;

export const getServerSideProps = popupMiddleware(async (ctx) => {
  const props = await getApolloSSRProps(
    MENU_QUERY.document(),
    { clock: ctx.query.clock ? String(ctx.query.clock) : null },
    ctx,
  );
  if (!props.__APOLLO_STATE_QUERY__.result.menu) {
    return { notFound: true };
  }
  return {
    props: {
      ...props,
      initialProductSlug: ctx.query.product ? String(ctx.query.product) : null,
    },
  };
});
